import type {
  DailyTruckCheckDetails,
  DailyTruckCheckTask,
  DailyTruckCheckTaskVerificationResult,
  TruckCheckDetails,
  TruckCheckTask,
  TruckCheckTaskVerificationResult
} from "./types";

function patchTasks<T extends { taskId: string; catalogItemId: string | null }>(
  current: T[],
  affected: T[],
  removedTaskIds: string[]
): T[] {
  const removed = new Set(removedTaskIds);
  const replacements = new Map(affected.map((task) => [task.taskId, task]));
  const next = current
    .filter((task) => !removed.has(task.taskId))
    .map((task) => replacements.get(task.taskId) ?? task);
  const existingIds = new Set(next.map((task) => task.taskId));

  for (const task of affected) {
    if (removed.has(task.taskId) || existingIds.has(task.taskId)) continue;

    let insertAt = next.length;
    for (let index = next.length - 1; index >= 0; index -= 1) {
      if (next[index].catalogItemId === task.catalogItemId) {
        insertAt = index + 1;
        break;
      }
    }
    next.splice(insertAt, 0, task);
    existingIds.add(task.taskId);
  }

  return next;
}

function locallyConsistentBags<
  T extends {
    tasks: Array<{ verified: boolean }>;
    totalTasks: number;
    verifiedTasks: number;
    complete: boolean;
  }
>(bags: T[]): {
  bags: T[];
  progress: {
    totalTasks: number;
    verifiedTasks: number;
    bagsTotal: number;
    bagsCompleted: number;
  };
} {
  const normalized = bags.map((bag) => {
    const totalTasks = bag.tasks.length;
    const verifiedTasks = bag.tasks.filter((task) => task.verified).length;
    return {
      ...bag,
      totalTasks,
      verifiedTasks,
      complete: totalTasks > 0 && verifiedTasks >= totalTasks
    };
  });

  return {
    bags: normalized,
    progress: {
      totalTasks: normalized.reduce((sum, bag) => sum + bag.totalTasks, 0),
      verifiedTasks: normalized.reduce((sum, bag) => sum + bag.verifiedTasks, 0),
      bagsTotal: normalized.length,
      bagsCompleted: normalized.filter((bag) => bag.complete).length
    }
  };
}

export function applyTruckCheckTaskVerification(
  current: TruckCheckDetails,
  result: TruckCheckTaskVerificationResult
): TruckCheckDetails {
  if (current.check.id !== result.checkId) return current;

  const patched = locallyConsistentBags(
    current.bags.map((bag) =>
      bag.bagId === result.bag.bagId
        ? {
            ...bag,
            ...result.bag,
            tasks: patchTasks<TruckCheckTask>(bag.tasks, result.affectedTasks, result.removedTaskIds)
          }
        : bag
    )
  );

  return {
    check: {
      ...current.check,
      updatedAt: result.checkRevision
    },
    progress: patched.progress,
    bags: patched.bags
  };
}

export function applyDailyTruckCheckTaskVerification(
  current: DailyTruckCheckDetails,
  result: DailyTruckCheckTaskVerificationResult
): DailyTruckCheckDetails {
  if (current.check.id !== result.checkId) return current;

  const patched = locallyConsistentBags(
    current.bags.map((bag) =>
      bag.bagId === result.bag.bagId
        ? {
            ...bag,
            ...result.bag,
            tasks: patchTasks<DailyTruckCheckTask>(bag.tasks, result.affectedTasks, result.removedTaskIds)
          }
        : bag
    )
  );

  return {
    check: {
      ...current.check,
      updatedAt: result.checkRevision
    },
    progress: patched.progress,
    bags: patched.bags
  };
}
